import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, Button, Card, CardContent, Divider, FormControl,
  FormLabel, Grid, IconButton, InputLabel,
  MenuItem, Paper, Select, TextField, Typography, Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAudience } from '../../hooks/useAudience';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';

const AudienceBuilder = ({ data, onUpdate }) => {
  const { fields, operators, previewAudience } = useAudience();
  const lastSubmittedRef = useRef(null);
  const initialRenderRef = useRef(true);
  const [error, setError] = useState(null);
  const [complexFilterSlug, setComplexFilterSlug] = useState(null);
  
  // Debug info about incoming data
  useEffect(() => {
    console.log("[AudienceBuilder] Received data:", data);
    
    // Check if this is a complex filter
    if (data?.complex_filter_slug) {
      console.log("[AudienceBuilder] Found complex_filter_slug:", data.complex_filter_slug);
      setComplexFilterSlug(data.complex_filter_slug);
      // Don't process further since complex filters don't use the normal condition builder
      return;
    }
    
    if (data?.groups) {
      console.log("[AudienceBuilder] Groups:", data.groups);
      data.groups.forEach((group, index) => {
        console.log(`[AudienceBuilder] Group ${index} (${group.type}):`, group.conditions);
      });
    }
  }, [data]);
  
  const [audience, setAudience] = useState(() => {
    // Convert from parent format to our internal format
    console.log("[AudienceBuilder] Initializing state with data:", data);
    
    // If this is a complex filter, set that up specifically
    if (data?.complex_filter_slug) {
      setComplexFilterSlug(data.complex_filter_slug);
      return {
        name: data.name || 'Complex Filter',
        complex_filter_slug: data.complex_filter_slug,
        conditions: [] // Empty conditions for complex filters
      };
    }
    
    if (!data) {
      return {
        name: '',
        conditions: []
      };
    }
    
    const initialConditions = [];
    
    // Process ALL conditions
    if (data?.groups && data.groups.length > 0) {
      const allGroup = data.groups.find(g => g.type === 'ALL');
      if (allGroup && Array.isArray(allGroup.conditions)) {
        console.log("[AudienceBuilder] Processing ALL conditions:", allGroup.conditions);
        allGroup.conditions.forEach(condition => {
          // Fix field and recordType format
          let recordType = condition.recordType;
          let fieldName = condition.fieldName;
          
          // If we have a field name but no record type, try to extract from field
          if (!recordType && fieldName && fieldName.includes('.')) {
            [recordType, fieldName] = fieldName.split('.');
          } 
          
          // If field contains record type, extract it
          if (fieldName && fieldName.includes('.')) {
            [recordType, fieldName] = fieldName.split('.');
          }
          
          initialConditions.push({
            ...condition,
            recordType: recordType, // Remove the default to 'client'
            fieldName: fieldName || condition.fieldName,
            id: `all-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            conditionGroup: 'ALL'
          });
        });
      }
      
      // Process ANY conditions
      const anyGroup = data.groups.find(g => g.type === 'ANY');
      if (anyGroup && Array.isArray(anyGroup.conditions)) {
        console.log("[AudienceBuilder] Processing ANY conditions:", anyGroup.conditions);
        anyGroup.conditions.forEach(condition => {
          // Fix field and recordType format
          let recordType = condition.recordType;
          let fieldName = condition.fieldName;
          
          // If we have a field name but no record type, try to extract from field
          if (!recordType && fieldName && fieldName.includes('.')) {
            [recordType, fieldName] = fieldName.split('.');
          } 
          
          // If field contains record type, extract it
          if (fieldName && fieldName.includes('.')) {
            [recordType, fieldName] = fieldName.split('.');
          }
          
          initialConditions.push({
            ...condition,
            recordType: recordType,
            fieldName: fieldName || condition.fieldName,
            id: `any-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            conditionGroup: 'ANY'
          });
        });
      }
    }
    
    console.log("[AudienceBuilder] Initial conditions:", initialConditions);
    
    return {
      name: data?.name ?? '',
      conditions: initialConditions
    };
  });
  
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResults, setPreviewResults] = useState(null);
  
  // Debug info about available fields
  useEffect(() => {
    if (fields && fields.length > 0) {
      // Log fields with SELECT type that have options
      const selectFields = fields.filter(f => f.type === 'select' && f.options && f.options.length > 0);
      console.log('Fields with dropdown options:', selectFields);
      
      // Log unique record types
      const recordTypes = [...new Set(fields.map(f => f.name.split('.')[0]))];
      console.log('Available record types:', recordTypes);
    }
  }, [fields]);

  // Check for prop updates after initial render
  useEffect(() => {
    if (!data) return;

    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return; // Skip first render as we already initialized state
    }

    console.log("[AudienceBuilder] Checking for data updates from props");

    // Only update in specific circumstances to avoid infinite loops
    const hasNoConditionsInState = !audience.conditions || audience.conditions.length === 0;
    const hasConditionsInProps = data?.groups?.some(group => 
      group.conditions && group.conditions.length > 0
    );
    
    // Check if there are invalid conditions in the state
    const invalidConditionsExist = audience.conditions?.some(c =>
      !c.recordType || !['client', 'appointment', 'service', 'invoice', 'quote'].includes(c.recordType)
    );
    
    // Count conditions in props
    const propConditionsCount = data.groups?.reduce(
      (count, group) => count + (group.conditions?.length || 0),
      0
    ) || 0;
    
    // Count conditions in state
    const stateConditionsCount = audience.conditions?.length || 0;
    
    // Update ONLY when needed to avoid loops
    const shouldUpdateFromProps = 
      // When state is empty but props have conditions
      (hasNoConditionsInState && hasConditionsInProps) || 
      // When there are invalid records in state
      invalidConditionsExist || 
      // When the condition counts are different by more than 1
      (Math.abs(propConditionsCount - stateConditionsCount) > 1);
    
    if (shouldUpdateFromProps) {
      console.log(`[AudienceBuilder] Updating from props: props=${propConditionsCount} conditions, state=${stateConditionsCount} conditions`);
      
      const updatedConditions = [];
      
      // Process ALL conditions
      const allGroup = data.groups.find(g => g.type === 'ALL');
      if (allGroup && Array.isArray(allGroup.conditions)) {
        allGroup.conditions.forEach(condition => {
          let recordType = condition.recordType;
          let fieldName = condition.fieldName;
          
          if (!recordType && fieldName && fieldName.includes('.')) {
            [recordType, fieldName] = fieldName.split('.');
          }
          
          if (fieldName && fieldName.includes('.')) {
            [recordType, fieldName] = fieldName.split('.');
          }
          
          updatedConditions.push({
            ...condition,
            recordType: recordType,
            fieldName: fieldName || condition.fieldName,
            id: `all-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            conditionGroup: 'ALL'
          });
        });
      }
      
      // Process ANY conditions
      const anyGroup = data.groups.find(g => g.type === 'ANY');
      if (anyGroup && Array.isArray(anyGroup.conditions)) {
        anyGroup.conditions.forEach(condition => {
          let recordType = condition.recordType;
          let fieldName = condition.fieldName;
          
          if (!recordType && fieldName && fieldName.includes('.')) {
            [recordType, fieldName] = fieldName.split('.');
          }
          
          if (fieldName && fieldName.includes('.')) {
            [recordType, fieldName] = fieldName.split('.');
          }
          
          updatedConditions.push({
            ...condition,
            recordType: recordType,
            fieldName: fieldName || condition.fieldName,
            id: `any-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            conditionGroup: 'ANY'
          });
        });
      }
      
      setAudience(prev => ({
        ...prev,
        name: data.name || prev.name,
        conditions: updatedConditions
      }));
    } else if (data.name !== audience.name) {
      // Only update the name when it changes
      setAudience(prev => ({
        ...prev,
        name: data.name
      }));
    }
  }, [data]);

  // Get available fields for a specific record type
  const getFieldsForRecordType = useCallback((recordType) => {
    if (!recordType || !fields || !fields.length) return [];
    
    const normalizedType = recordType.toLowerCase();
    
    const filteredFields = fields.filter(field => {
      // Extract the table part from field name (e.g., "client" from "client.first_name")
      const fieldTable = field.name.split('.')[0];
      return fieldTable.toLowerCase() === normalizedType;
    });
    
    // If no fields found, log useful debug info
    if (filteredFields.length === 0) {
      console.log(`No fields found for record type: ${recordType}`);
      console.log('Available fields:', fields.map(f => f.name));
      console.log('Available record types:', [...new Set(fields.map(f => f.name.split('.')[0]))]);
      
      // Create placeholder fields for testing if needed
      if (process.env.NODE_ENV === 'development') {
        const placeholderFields = [
          { name: `${normalizedType}.id`, label: 'ID', type: 'text' },
          { name: `${normalizedType}.name`, label: 'Name', type: 'text' },
          { name: `${normalizedType}.date`, label: 'Date', type: 'date' },
          { name: `${normalizedType}.status`, label: 'Status', type: 'select', options: [
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' }
          ] },
          { name: `${normalizedType}.amount`, label: 'Amount', type: 'number' }
        ];
        
        return placeholderFields;
      }
    }
    
    return filteredFields;
  }, [fields]);

  const formatAudienceForParent = useCallback((audienceData) => {
    // If this is a complex filter, just pass that information back
    if (complexFilterSlug) {
      return {
        name: audienceData.name || 'Complex Filter',
        complex_filter_slug: complexFilterSlug
      };
    }
    
    // Transform our internal format to match what the parent expects
    const formatted = {
      name: audienceData.name,
      groups: [
        {
          type: 'ALL',
          conditions: audienceData.conditions
            .filter(c => c.conditionGroup === 'ALL')
            .map(c => ({
              recordType: c.recordType,
              fieldName: c.fieldName,
              operator: c.operator,
              value: c.value
            }))
        },
        {
          type: 'ANY',
          conditions: audienceData.conditions
            .filter(c => c.conditionGroup === 'ANY')
            .map(c => ({
              recordType: c.recordType,
              fieldName: c.fieldName,
              operator: c.operator,
              value: c.value
            }))
        }
      ]
    };
    
    console.log("[AudienceBuilder] Formatted audience for parent:", formatted);
    return formatted;
  }, [complexFilterSlug]);
  
  // Notify parent of changes, but only if the data has actually changed
  const notifyParentOfChanges = useCallback(() => {
    const formattedData = formatAudienceForParent(audience);
    // Only update if the data has actually changed
    if (!lastSubmittedRef.current || !isEqual(lastSubmittedRef.current, formattedData)) {
      lastSubmittedRef.current = cloneDeep(formattedData);
      console.log("[AudienceBuilder] Notifying parent of changes:", formattedData);
      if (typeof onUpdate === 'function') {
        onUpdate(formattedData);
      }
    }
  }, [audience, formatAudienceForParent, onUpdate]);
  
  const handleFilterNameChange = useCallback((e) => {
    const newName = e.target.value;

    setAudience(prev => ({
      ...prev,
      name: newName
    }));

    const formattedData = {
      name: newName,
      groups: [
        {
          type: 'ALL',
          conditions: audience.conditions
            .filter(c => c.conditionGroup === 'ALL')
            .map(c => ({
              recordType: c.recordType,
              fieldName: c.fieldName,
              operator: c.operator,
              value: c.value
            }))
        },
        {
          type: 'ANY',
          conditions: audience.conditions
            .filter(c => c.conditionGroup === 'ANY')
            .map(c => ({
              recordType: c.recordType,
              fieldName: c.fieldName,
              operator: c.operator,
              value: c.value
            }))
        }
      ]
    };

    lastSubmittedRef.current = cloneDeep(formattedData);

    console.log("[AudienceBuilder] Name changed, notifying parent:", newName);
    if (typeof onUpdate === 'function') {
      onUpdate(formattedData);
    }
  }, [audience.conditions, onUpdate]);

  const addCondition = useCallback((group) => {
    const newCondition = {
      id: `${group.toLowerCase()}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      recordType: '',
      fieldName: '',
      operator: '',
      value: '',
      conditionGroup: group
    };
    
    setAudience(prevAudience => {
      // Create a new conditions array with the added condition
      const updatedConditions = [
        ...(Array.isArray(prevAudience.conditions) ? prevAudience.conditions : []),
        newCondition
      ];
      
      // Return new audience state with updated conditions
      return {
        ...prevAudience,
        conditions: updatedConditions
      };
    });
    
    // Notify parent after state has been updated, with delay to ensure state is consistent
    setTimeout(() => {
      setAudience(current => {
        const formattedData = formatAudienceForParent(current);
        if (typeof onUpdate === 'function' && (!lastSubmittedRef.current || !isEqual(lastSubmittedRef.current, formattedData))) {
          lastSubmittedRef.current = cloneDeep(formattedData);
          console.log("[AudienceBuilder] Added new condition, notifying parent:", formattedData);
          onUpdate(formattedData);
        }
        return current; // Return unchanged state
      });
    }, 50);
  }, [formatAudienceForParent, onUpdate]);

  const updateCondition = useCallback((id, field, value) => {
    console.log(`[AudienceBuilder] Updating condition ${id}, field: ${field}, value:`, value);
  
    // Create a new conditions array with the updated condition
    setAudience(prevAudience => {
      console.log('[AudienceBuilder] Current audience state before update:', prevAudience);
      
      const updatedConditions = prevAudience.conditions.map(condition => {
        if (condition.id === id) {
          console.log('[AudienceBuilder] Found condition to update:', condition);
          console.log(`[AudienceBuilder] Changing ${field} from "${condition[field]}" to "${value}"`);
          
          const updatedCondition = { ...condition, [field]: value };
          return updatedCondition;
        }
        return condition;
      });
      
      const updatedAudience = {
        ...prevAudience,
        conditions: updatedConditions
      };
      
      console.log('[AudienceBuilder] Updated audience state:', updatedAudience);
      
      // Notify parent after a short delay to allow state to update
      setTimeout(() => {
        if (typeof onUpdate === 'function') {
          const formattedData = formatAudienceForParent(updatedAudience);
          console.log('[AudienceBuilder] Notifying parent of update:', formattedData);
          onUpdate(formattedData);
        }
      }, 0);
      
      return updatedAudience;
    });
  }, [onUpdate, formatAudienceForParent]);

  const removeCondition = useCallback((id) => {
    setAudience(prevAudience => ({
      ...prevAudience,
      conditions: prevAudience.conditions.filter(condition => condition.id !== id)
    }));
    
    // Notify parent after a short delay to allow state to update
    setTimeout(() => {
      notifyParentOfChanges();
    }, 100);
  }, [notifyParentOfChanges]);

  // Determine the field type for a selected condition
  const getFieldType = useCallback((condition) => {
    if (!condition.recordType || !condition.fieldName) return 'text';
    
    // Special handling for service_id which should always be a select
    if (condition.fieldName === 'service_id') {
      return 'select';
    }
    
    // Find the matching field definition from the backend
    const availableFields = getFieldsForRecordType(condition.recordType);
    const fieldDef = availableFields.find(f => {
      const fieldColumn = f.name.split('.')[1];
      return fieldColumn === condition.fieldName;
    });
    
    if (!fieldDef) return 'text';
    
    // Trust the field type from the backend definition
    return fieldDef.type || 'text';
  }, [getFieldsForRecordType]);
  
  // Get available options for a select field
  const getFieldOptions = useCallback((condition) => {
    if (!condition.recordType || !condition.fieldName) return [];
    
    // Find the matching field definition from the backend
    const availableFields = getFieldsForRecordType(condition.recordType);
    const fieldDef = availableFields.find(f => {
      const fieldColumn = f.name.split('.')[1];
      return fieldColumn === condition.fieldName;
    });
    
    if (!fieldDef || !fieldDef.options) {
      console.log(`No options found for field: ${condition.recordType}.${condition.fieldName}`);
      return [];
    }
    
    return fieldDef.options;
  }, [getFieldsForRecordType]);
  
  // Render the appropriate value input based on field type and operator
  const renderValueInput = useCallback((condition) => {
    // If operator doesn't need a value, don't render anything
    if (['IS_EMPTY', 'IS_NOT_EMPTY'].includes(condition.operator)) {
      return (
        <TextField
          fullWidth
          label="Value"
          disabled
          value="Not required for this operator"
          sx={{ minHeight: '56px' }}
        />
      );
    }
    
    const fieldType = getFieldType(condition);
    console.log(`Field type for ${condition.recordType}.${condition.fieldName}: ${fieldType}`);
  
    // Handle special date operators that need number input
    const dateOperatorsWithNumberInput = [
      'IS_X_DAYS_AGO',
      'IS_MORE_THAN_X_DAYS_AGO', 
      'IS_LESS_THAN_X_DAYS_AGO',
      'IS_IN_X_DAYS',
      'IS_MORE_THAN_X_DAYS_AWAY',
      'IS_LESS_THAN_X_DAYS_AWAY'
    ];
    
    if (
      dateOperatorsWithNumberInput.includes(condition.operator) &&
      getFieldType(condition) === 'date'
    ) {
      // Determine the appropriate label and helper text based on operator
      let label = "Days";
      let helperText = "Enter number of days";
      
      if (condition.operator.includes('AGO')) {
        label = "Days Ago";
        helperText = "Enter number of days in the past (e.g., 7 for 7 days ago)";
      } else if (condition.operator.includes('AWAY') || condition.operator.includes('IN_X_DAYS')) {
        label = "Days Away";
        helperText = "Enter number of days in the future (e.g., 7 for 7 days from now)";
      }
      
      return (
        <TextField
          fullWidth
          label={label}
          type="number"
          value={condition.value || ''}
          onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
          disabled={!condition.operator}
          inputProps={{ 
            min: 1, 
            max: 3650, // Max ~10 years seems reasonable
            step: 1 
          }}
          helperText={helperText}
          sx={{ minHeight: '56px' }}
        />
      );
    }
    
    // Common styles for input components
    const inputStyle = {
      minHeight: '56px'
    };
    
    switch(fieldType) {
      case 'date':
        return (
          <TextField
            fullWidth
            label="Value"
            type="date"
            value={condition.value || ''}
            onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
            disabled={!condition.operator}
            InputLabelProps={{ shrink: true }}
            sx={inputStyle}
          />
        );
        
      case 'number':
        return (
          <TextField
            fullWidth
            label="Value"
            type="number"
            value={typeof condition.value === 'string' ? condition.value : ''}
            onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
            disabled={!condition.operator}
            sx={inputStyle}
          />
        );
        
      case 'multi_select':
        const options = getFieldOptions(condition);
        return (
          <FormControl fullWidth>
            <InputLabel>Value</InputLabel>
            <Select
              multiple
              value={Array.isArray(condition.value) ? condition.value : []}
              label="Value"
              onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
              disabled={!condition.operator || ['IS_EMPTY', 'IS_NOT_EMPTY'].includes(condition.operator)}
              sx={inputStyle}
              renderValue={(selected) => {
                if (selected.length === 0) return '';
                const selectedLabels = selected.map(value => {
                  const option = options.find(opt => opt.value === value);
                  return option ? option.label : value;
                });
                return selectedLabels.join(', ');
              }}
            >
              {options.length > 0 ? (
                options.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>No options available</MenuItem>
              )}
            </Select>
          </FormControl>
        );
        
      case 'select':
        const selectOptions = getFieldOptions(condition);
        return (
          <FormControl fullWidth>
            <InputLabel>Value</InputLabel>
            <Select
              value={condition.value || ''}
              label="Value"
              onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
              disabled={!condition.operator}
              sx={inputStyle}
            >
              {selectOptions.length > 0 ? (
                selectOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>No options available</MenuItem>
              )}
            </Select>
          </FormControl>
        );
        
      case 'boolean':
        return (
          <FormControl fullWidth>
            <InputLabel>Value</InputLabel>
            <Select
              value={condition.value === '' ? '' : String(Boolean(condition.value))}
              label="Value"
              onChange={(e) => updateCondition(condition.id, 'value', e.target.value === 'true')}
              disabled={!condition.operator}
              sx={inputStyle}
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        );
        
      case 'multi_select':
        const multiSelectOptions = getFieldOptions(condition);
        return (
          <FormControl fullWidth>
            <InputLabel>Value</InputLabel>
            <Select
              multiple
              value={Array.isArray(condition.value) ? condition.value : []}
              label="Value"
              onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
              disabled={!condition.operator || ['IS_EMPTY', 'IS_NOT_EMPTY'].includes(condition.operator)}
              sx={inputStyle}
              renderValue={(selected) => {
                if (selected.length === 0) return '';
                const selectedLabels = selected.map(value => {
                  const option = multiSelectOptions.find(opt => opt.value === value);
                  return option ? option.label : value;
                });
                return selectedLabels.join(', ');
              }}
            >
              {multiSelectOptions.length > 0 ? (
                multiSelectOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>No options available</MenuItem>
              )}
            </Select>
          </FormControl>
        );
        
      case 'text':
      default:
        return (
          <TextField
            fullWidth
            label="Value"
            value={typeof condition.value === 'string' ? condition.value : ''}
            onChange={(e) => updateCondition(condition.id, 'value', e.target.value)}
            disabled={!condition.operator}
            sx={inputStyle}
          />
        );
    }
  }, [getFieldOptions, getFieldType, updateCondition]);

  // Prepare condition value for API based on field type
  const prepareValueForAPI = useCallback((condition) => {
    const fieldType = getFieldType(condition);
    const value = condition.value;
    
    if (value === null || value === undefined || value === '') {
      return null;
    }
    
    // Special handling for date operators that need numeric values
    const dateOperatorsWithNumberInput = [
      'IS_X_DAYS_AGO',
      'IS_MORE_THAN_X_DAYS_AGO', 
      'IS_LESS_THAN_X_DAYS_AGO',
      'IS_IN_X_DAYS',
      'IS_MORE_THAN_X_DAYS_AWAY',
      'IS_LESS_THAN_X_DAYS_AWAY'
    ];
    
    if (dateOperatorsWithNumberInput.includes(condition.operator)) {
      return parseInt(value, 10);
    }
    
    switch(fieldType) {
      case 'number':
        return parseFloat(value);
        
      case 'date':
        // Ensure date is in ISO format
        return new Date(value).toISOString().split('T')[0];
        
      case 'boolean':
        return Boolean(value);
        
      case 'multi_select':
        // Ensure we return an array for multi-select fields
        if (Array.isArray(value)) {
          return value;
        } else if (typeof value === 'string' && value.includes(',')) {
          return value.split(',').map(v => v.trim());
        } else if (value) {
          return [value];
        }
        return [];
        
      default:
        return value;
    }
  }, [getFieldType]);
  
  // Handle preview audience
  const handlePreviewAudience = useCallback(async () => {
    if (audience.conditions.filter(c => c.conditionGroup === 'ALL').length === 0) {
      setError('Please add at least one required condition (ALL group) before previewing.');
      return;
    }
    
    setError(null);
    
    // Transform the audience data to the format expected by the API
    const apiAudienceData = {
      match_all: [{
        conjunction: "AND",
        conditions: audience.conditions
          .filter(c => c.conditionGroup === 'ALL' && c.recordType && c.fieldName && c.operator)
          .map(c => {
            let field;
            if (c.recordType === 'appointment' && c.fieldName === 'service_id') {
              field = 'appointment_service.service_id';
              if (!['EQUALS', 'NOT_EQUALS', 'IS_EMPTY', 'IS_NOT_EMPTY'].includes(c.operator)) {
                console.warn(`Invalid operator ${c.operator} used for service_id, defaulting to EQUALS`);
                c.operator = 'EQUALS';
              }
            } else if (c.recordType === 'service_category' && c.fieldName === 'name') {
              field = 'service_category.name';
            } else {
              let entityType = c.recordType;
              if (c.fieldName === 'start_date' || c.fieldName === 'end_date') {
                entityType = 'appointment';
              } else if (['first_name', 'last_name', 'email', 'date_of_birth'].includes(c.fieldName)) {
                entityType = 'client';
              }
              field = `${entityType}.${c.fieldName}`;
            }
            
            return {
              field: field,
              operator: c.operator,
              value: prepareValueForAPI(c)
            };
          })
      }],
      match_any: [{
        conjunction: "OR",
        conditions: audience.conditions
          .filter(c => c.conditionGroup === 'ANY' && c.recordType && c.fieldName && c.operator)
          .map(c => {
            let field;
            if (c.recordType === 'appointment' && c.fieldName === 'service_id') {
              field = 'appointment_service.service_id';
              if (!['EQUALS', 'NOT_EQUALS', 'IS_EMPTY', 'IS_NOT_EMPTY'].includes(c.operator)) {
                console.warn(`Invalid operator ${c.operator} used for service_id, defaulting to EQUALS`);
                c.operator = 'EQUALS';
              }
            } else if (c.recordType === 'service_category' && c.fieldName === 'name') {
              field = 'service_category.name';
            } else {
              let entityType = c.recordType;
              if (c.fieldName === 'start_date' || c.fieldName === 'end_date') {
                entityType = 'appointment';
              } else if (['first_name', 'last_name', 'email', 'date_of_birth'].includes(c.fieldName)) {
                entityType = 'client';
              }
              field = `${entityType}.${c.fieldName}`;
            }
            
            return {
              field: field,
              operator: c.operator,
              value: prepareValueForAPI(c)
            };
          })
      }]
    };
    
    // Add detailed logging to diagnose the issue
    console.log('API data as JSON string:', JSON.stringify(apiAudienceData, null, 2));
    
    setPreviewLoading(true);
    try {
      console.log('Sending preview request with data:', apiAudienceData);
      const results = await previewAudience(apiAudienceData);
      console.log('Preview results:', results);
      setPreviewResults(results);
      
      // Notify parent of changes after successful preview
      notifyParentOfChanges();
    } catch (error) {
      console.error('Error previewing audience:', error);
      // More detailed error logging
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      setError(`Error previewing audience: ${error.message || 'Unknown error'}`);
    } finally {
      setPreviewLoading(false);
    }
  }, [audience, notifyParentOfChanges, prepareValueForAPI, previewAudience]);

  // When filter name changes, notify the parent
  useEffect(() => {
    // Skip during initial render
    if (initialRenderRef.current) {
      return;
    }
    
    // Only notify for name changes
    const timer = setTimeout(() => {
      if (audience.name !== data?.name) {
        notifyParentOfChanges();
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [audience.name, data?.name, notifyParentOfChanges]);

  // Render condition form
  const renderConditionForm = useCallback((condition, index) => {
    // Get fields available for the selected record type
    const availableFields = getFieldsForRecordType(condition.recordType);
    
    // Common styles for form controls
    const formControlStyle = {
      minWidth: '200px',  // Set minimum width
      width: '100%'      // Take full available width
    };
    
    return (
      <Grid container spacing={2} key={condition.id} sx={{ mb: 2 }}>
        {/* Record Type Dropdown */}
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth style={formControlStyle}>
            <InputLabel>Record Type</InputLabel>
            <Select
              value={condition.recordType || ''}
              label="Record Type"
              onChange={(e) => updateCondition(condition.id, 'recordType', e.target.value)}
              sx={{ minHeight: '56px' }}  // Set minimum height for consistency
            >
              {['client', 'appointment', 'service', 'service_category', 'invoice', 'quote'].map(type => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Field Name Dropdown */}
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth disabled={!condition.recordType} style={formControlStyle}>
            <InputLabel>Field</InputLabel>
            <Select
              value={condition.fieldName || ''}
              label="Field"
              onChange={(e) => updateCondition(condition.id, 'fieldName', e.target.value)}
              sx={{ minHeight: '56px' }}
              displayEmpty
            >
              {condition.recordType ? (
                availableFields.length > 0 ? (
                  availableFields.map(field => {
                    // Extract the column part from field name (e.g., "first_name" from "client.first_name")
                    const fieldColumn = field.name.split('.')[1];
                    return (
                      <MenuItem key={field.name} value={fieldColumn} onClick={() => {
                        const entityType = field.name.split('.')[0];
                        updateCondition(condition.id, 'recordType', entityType);
                        updateCondition(condition.id, 'fieldName', fieldColumn);
                      }}>
                        {field.label}
                      </MenuItem>
                    );
                  })
                ) : (
                  <MenuItem disabled value="">
                    <em>No fields available for this record type</em>
                  </MenuItem>
                )
              ) : (
                <MenuItem disabled value="">
                  <em>Select a record type first</em>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Operator Dropdown */}
        <Grid item xs={12} sm={2}>
          <FormControl fullWidth disabled={!condition.fieldName} style={formControlStyle}>
            <InputLabel>Operator</InputLabel>
            <Select
              value={condition.operator || ''}
              label="Operator"
              onChange={(e) => updateCondition(condition.id, 'operator', e.target.value)}
              sx={{ minHeight: '56px' }}
              displayEmpty
            >
              {condition.fieldName ? (
                operators.length > 0 ? (
                  operators
                    .filter(op => {
                      // Get the current field type
                      const fieldType = getFieldType(condition);
                      
                      // For multi_select fields (service_category), show all relevant operators
                      if (fieldType === 'multi_select') {
                        return ['IS_ONE_OF', 'IS_NOT_ONE_OF', 'IS_EMPTY', 'IS_NOT_EMPTY'].includes(op.value);
                      }
                      // For select fields, only allow certain operators
                      if (fieldType === 'select') {
                        return ['EQUALS', 'NOT_EQUALS', 'IS_EMPTY', 'IS_NOT_EMPTY'].includes(op.value);
                      }
                      return true;
                    })
                    .map(op => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem disabled value="">
                    <em>No operators available</em>
                  </MenuItem>
                )
              ) : (
                <MenuItem disabled value="">
                  <em>Select a field first</em>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Value Field - Dynamic based on field type */}
        <Grid item xs={12} sm={3}>
          <Box style={formControlStyle}>
            {condition.operator ? (
              renderValueInput(condition)
            ) : (
              <TextField
                fullWidth
                label="Value"
                disabled
                value={typeof condition.value === 'string' ? condition.value : ''}
                placeholder="Select an operator first"
                sx={{ minHeight: '56px' }}
              />
            )}
          </Box>
        </Grid>
        
        {/* Delete Button */}
        <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            color="error" 
            onClick={() => removeCondition(condition.id)}
          >
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    );
  }, [operators, getFieldsForRecordType, getFieldType, renderValueInput, removeCondition, updateCondition]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Build Your Audience
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {complexFilterSlug ? (
        // Show a message for complex filters instead of the builder UI
        <Paper sx={{ p: 3, mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle1">
              Complex Filter Selected
            </Typography>
            <Typography variant="body2">
              You are using a complex filter that was configured in the system.
              This type of audience filter is managed separately and cannot be modified here.
            </Typography>
          </Alert>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mr: 2 }}>
              Filter Name:
            </Typography>
            <TextField
              value={audience.name}
              onChange={handleFilterNameChange}
              variant="outlined"
              size="small"
              sx={{ flexGrow: 1 }}
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary">
            Complex Filter ID: <code>{complexFilterSlug}</code>
          </Typography>
        </Paper>
      ) : (
        // Show the normal audience builder UI for regular audiences
        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            fullWidth
            label="Filter Name"
            value={audience.name}
            onChange={handleFilterNameChange}
            sx={{ mb: 3 }}
          />
          
          <Typography variant="h6" gutterBottom>
            Conditions
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              ALL of these conditions:
            </Typography>
            
            {(audience?.conditions || [])
              .filter(c => c.conditionGroup === 'ALL')
              .map((condition, index) => renderConditionForm(condition, index))}
            
            <Button 
              startIcon={<AddIcon />} 
              onClick={() => addCondition('ALL')}
              variant="outlined"
              sx={{ mt: 1 }}
            >
              Add Required Condition
            </Button>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              ANY of these conditions:
            </Typography>
            
            {(audience?.conditions || [])
              .filter(c => c.conditionGroup === 'ANY')
              .map((condition, index) => renderConditionForm(condition, index))}
            
            <Button 
              startIcon={<AddIcon />} 
              onClick={() => addCondition('ANY')}
              variant="outlined"
              sx={{ mt: 1 }}
            >
              Add Optional Condition
            </Button>
          </Box>
        </Paper>
      )}
      
      {!complexFilterSlug && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Preview Results</Typography>
            <Button 
              variant="contained" 
              onClick={handlePreviewAudience}
              disabled={previewLoading || (audience?.conditions?.filter(c => c.conditionGroup === 'ALL').length === 0)}
            >
              {previewLoading ? 'Loading...' : 'Preview Audience'}
            </Button>
          </Box>
          
          {previewResults && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {previewResults.matched_count} clients match your criteria
              </Typography>
              
              <Grid container spacing={2}>
                {previewResults.sample_clients.map((client, index) => (
                  <Grid item xs={12} sm={6} md={4} key={client.id || `client-${index}`}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">
                          {client.name}
                        </Typography>
                        <Typography color="textSecondary">
                          {client.email}
                        </Typography>
                        {client.last_appointment_date && (
                          <Typography variant="body2">
                            Last appointment: {new Date(client.last_appointment_date).toLocaleDateString()}
                          </Typography>
                        )}
                        {client.last_service && (
                          <Typography variant="body2">
                            Service: {client.last_service}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};


AudienceBuilder.defaultProps = {
  onUpdate: () => {},
};

export default AudienceBuilder;